import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Home, CheckCircle2, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import liveStayLogo from "@/assets/live-stay-logo.png";
import InputMask from "react-input-mask";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CountrySelect, Country } from "@/components/CountrySelect";
import { getApiEndpoint } from "@/config/api";
import Footer from "@/components/Footer";
import AuthProfileBanner from "@/components/auth/AuthProfileBanner";
import { resolveAuthProfileTheme } from "@/lib/authProfileTheme";
import { cn } from "@/lib/utils";

const resolveInitialTab = (): "login" | "cadastro" => {
  const hash = window.location.hash.replace("#", "");
  if (hash === "cadastro" || hash === "cadastrar") return "cadastro";
  return "login";
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const profileParam = searchParams.get("profile");
  const expectedProfile = (
    profileParam === "client" || profileParam === "owner" || profileParam === "admin"
      ? profileParam
      : searchParams.get("type") === "owner"
        ? "owner"
        : null
  ) as "client" | "owner" | "admin" | null;
  const isOwnerProfile = expectedProfile === "owner";
  const isAdminProfile = expectedProfile === "admin";
  const isOwnerLogin = isOwnerProfile;
  const isClientLogin = expectedProfile === "client";
  const isAdminLogin = isAdminProfile;
  const authProfileTheme = resolveAuthProfileTheme(expectedProfile);

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [telefone, setTelefone] = useState("");
  const [phoneDdi, setPhoneDdi] = useState("55"); // Brasil como padrão
  const [partnerReferralToken, setPartnerReferralToken] = useState("");
  const [lembrarSenha, setLembrarSenha] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "cadastro">(resolveInitialTab);
  const isOwnerRegistration = isOwnerProfile && activeTab === "cadastro";
  const [senhaErrors, setSenhaErrors] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  // Verificar âncora na URL para abrir a aba correta
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'cadastro' || hash === 'cadastrar') {
      setActiveTab('cadastro');
    } else if (hash === 'login' || hash === 'entrar') {
      setActiveTab('login');
    }
  }, []);

  useEffect(() => {
    if (isAdminProfile) {
      setActiveTab("login");
    }
  }, [isAdminProfile]);

  // Carregar credenciais salvas quando a página for carregada
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
    const rememberPassword = localStorage.getItem("rememberPassword");

    if (rememberedEmail && rememberedPassword && rememberPassword === "true") {
      setEmail(rememberedEmail);
      setSenha(rememberedPassword);
      setLembrarSenha(true);
    }
  }, []);

  // Validação de email
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validação de senha
  const validatePassword = (password: string) => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push("Mínimo de 8 caracteres");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Pelo menos 1 letra minúscula");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Pelo menos 1 letra maiúscula");
    }
    
    if (!/\d/.test(password)) {
      errors.push("Pelo menos 1 número");
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("Pelo menos 1 caractere especial");
    }
    
    return errors;
  };

  // Validação de confirmação de senha
  const validatePasswordConfirmation = (password: string, confirmation: string) => {
    return password === confirmation;
  };

  // Validação do token de indicação (16 caracteres alfanuméricos em maiúsculas)
  const validateReferralToken = (token: string) => {
    if (!token || token.trim() === '') {
      return true; // Campo não obrigatório
    }
    const tokenRegex = /^[A-Z0-9]{16}$/;
    return tokenRegex.test(token);
  };

  // Handler para token de indicação (converter para maiúsculas e limitar a 16 caracteres)
  const handleReferralTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); // Apenas letras e números, em maiúsculas
    if (value.length > 16) {
      value = value.substring(0, 16);
    }
    setPartnerReferralToken(value);
  };

  // Handlers para validação em tempo real
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenha(e.target.value);
    if (senhaErrors.length > 0) {
      setSenhaErrors([]);
    }
  };

  const checkUserApprovalStatus = async (email: string) => {
    try {
      const response = await fetch(getApiEndpoint('/api/users/check-approval'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch (error) {
      console.error('Erro ao verificar status de aprovação:', error);
      return null;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const authResponse = await authService.login({
        email,
        password: senha,
      });

      if (authResponse) {
        const loggedUserType = authResponse.user.user_type;

        if (loggedUserType === "admin") {
          if (isAdminLogin) {
            if (lembrarSenha) {
              localStorage.setItem("rememberedEmail", email);
              localStorage.setItem("rememberedPassword", senha);
              localStorage.setItem("rememberPassword", "true");
            } else {
              localStorage.removeItem("rememberedEmail");
              localStorage.removeItem("rememberedPassword");
              localStorage.removeItem("rememberPassword");
            }

            toast({
              title: "Login realizado!",
              description: "Bem-vindo à área administrativa.",
            });
            navigate("/admin/dashboard");
            setLoading(false);
            return;
          }

          authService.logout();
          toast({
            title: "Acesso Administrativo",
            description: "Administradores devem entrar pela opção Administrador em /entrar.",
            variant: "destructive",
          });
          setTimeout(() => {
            navigate("/login?profile=admin");
          }, 1500);
          setLoading(false);
          return;
        }

        if (isAdminLogin && loggedUserType !== "admin") {
          authService.logout();
          toast({
            title: "Conta sem permissão administrativa",
            description: "Este e-mail não possui acesso de administrador.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        if (isOwnerLogin && loggedUserType !== "owner") {
          authService.logout();
          toast({
            title: "Conta de Hóspede detectada",
            description: "Este e-mail pertence a uma conta de Hóspede. Use a opção Entrar como Hóspede.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        if (isClientLogin && expectedProfile === "client" && loggedUserType === "owner") {
          authService.logout();
          toast({
            title: "Conta de Proprietário detectada",
            description: "Este e-mail pertence a uma conta de Proprietário. Use a opção Entrar como Proprietário.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        
        // Salvar credenciais se "lembrar senha" estiver marcado
        if (lembrarSenha) {
          localStorage.setItem("rememberedEmail", email);
          localStorage.setItem("rememberedPassword", senha);
          localStorage.setItem("rememberPassword", "true");
        } else {
          // Remover credenciais salvas se "lembrar senha" não estiver marcado
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberedPassword");
          localStorage.removeItem("rememberPassword");
        }

        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!",
        });
        
        // Redirecionar para página principal
        navigate("/");
      } else {
        // Verificar se o usuário está pendente de aprovação
        const userStatus = await checkUserApprovalStatus(email);
        
        if (userStatus && userStatus.user_type === 'owner' && userStatus.approval_status === 'pending') {
          toast({
            title: "Acesso pendente de aprovação",
            description: "Seu cadastro está aguardando aprovação. Você receberá um email quando for aprovado para anunciar no site.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro ao fazer login",
            description: "Email ou senha incorretos",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Erro ao fazer login",
        description: error.message || "Erro interno do servidor",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Limpar erros anteriores
    setEmailError("");
    setSenhaErrors([]);

    // Validar email
    if (!validateEmail(email)) {
      setEmailError("Por favor, insira um email válido");
      setLoading(false);
      return;
    }

    // Validar senha
    const passwordErrors = validatePassword(senha);
    if (passwordErrors.length > 0) {
      setSenhaErrors(passwordErrors);
      setLoading(false);
      return;
    }

    // Validar confirmação de senha
    if (!validatePasswordConfirmation(senha, confirmarSenha)) {
      toast({
        title: "Erro de validação",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Validar consentimento com a Política de Privacidade
    if (!privacyConsent) {
      toast({
        title: "Consentimento necessário",
        description: "Você precisa aceitar a Política de Privacidade para criar uma conta.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      const userType = isOwnerRegistration ? "owner" : "guest";

      // Validar token de indicação se fornecido (apenas para proprietários)
      if (userType === "owner" && partnerReferralToken && !validateReferralToken(partnerReferralToken)) {
        toast({
          title: "Token inválido",
          description: "O token de indicação deve ter exatamente 16 caracteres alfanuméricos.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const authResponse = await authService.register({
        email,
        password_hash: senha,
        name: nomeCompleto,
        phone: telefone,
        phone_ddi: phoneDdi,
        user_type: userType,
        privacy_consent: privacyConsent,
        partner_referral_token: userType === "owner" && partnerReferralToken ? partnerReferralToken.trim() : null,
      });

      if (userType === "owner") {
        // Proprietário criado mas não logado (status pendente)
        setRegisteredEmail(email);
        setShowSuccessMessage(true);
        toast({
          title: "Cadastro realizado com sucesso!",
          description: "Seu cadastro foi efetuado com sucesso. Porém, ele passa por um processo de aprovação para que você possa anunciar no site.",
        });
      } else {
        // Usuário comum logado automaticamente
        if (authResponse && authResponse.token) {
          toast({
            title: "Cadastro realizado!",
            description: "Conta criada com sucesso!",
          });
          navigate("/");
        } else {
          toast({
            title: "Erro no cadastro",
            description: "Erro ao criar conta",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Erro ao criar conta",
        description: error.message || "Erro interno do servidor",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  // Se mostrar mensagem de sucesso, exibir apenas ela
  if (showSuccessMessage && isOwnerRegistration) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <div className="text-center mb-8">
              <img 
                src={liveStayLogo} 
                alt="Live Stay" 
                className="h-20 mx-auto mb-6"
              />
            </div>

            <Card className="shadow-large border-2 border-primary/20">
              <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Cadastro Realizado com Sucesso!
                </h2>
                <p className="text-muted-foreground">
                  Seu cadastro foi efetuado com sucesso
                </p>
              </div>

              <Alert className="mb-6 border-primary/50 bg-primary/5">
                <Clock className="h-5 w-5 text-primary" />
                <AlertTitle className="text-lg font-semibold text-foreground mb-2">
                  Aguardando Aprovação
                </AlertTitle>
                <AlertDescription className="text-base space-y-3">
                  <p className="font-medium text-foreground">
                    Seu cadastro passará por um processo de validação antes de ser aprovado.
                  </p>
                  <div className="bg-background/50 rounded-lg p-4 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">O que acontece agora?</strong>
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1.5 ml-4 list-disc">
                      <li>Nossa equipe irá analisar seu cadastro</li>
                      <li>Após a aprovação, você poderá anunciar seus imóveis no site</li>
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex justify-center">
                <Button
                  onClick={() => navigate("/")}
                  className="gap-2"
                  size="lg"
                >
                  <Home className="h-4 w-4" />
                  Voltar para o Site
                </Button>
              </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col",
        authProfileTheme
          ? `bg-gradient-to-br ${authProfileTheme.pageGradient}`
          : "bg-gradient-to-br from-primary/10 via-background to-secondary/10",
      )}
    >
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(expectedProfile ? (activeTab === "cadastro" ? "/cadastro" : "/entrar") : "/")}
            className="mb-4 gap-2"
          >
            <Home className="h-4 w-4" />
            Voltar
          </Button>
          <img 
            src={liveStayLogo} 
            alt="Live Stay" 
            className="h-20 mx-auto mb-4"
          />
          {authProfileTheme && (
            <div className="mb-4">
              <AuthProfileBanner theme={authProfileTheme} compact />
            </div>
          )}
          <p className="text-muted-foreground mt-2">
            {isAdminProfile
              ? "Entre com suas credenciais administrativas para acessar o painel da LiveStay."
              : isOwnerProfile
                ? activeTab === "cadastro"
                  ? "Cadastre-se como proprietário para anunciar seu imóvel na LiveStay."
                  : "Entre com sua conta de proprietário para gerenciar anúncios e reservas."
                : expectedProfile === "client"
                  ? activeTab === "cadastro"
                    ? "Cadastre-se como hóspede para reservar estadias e usar sua área pessoal."
                    : "Entre com sua conta de hóspede para reservar estadias e acompanhar reservas."
                  : "Entre ou cadastre-se para continuar"}
          </p>
        </div>

          <Card
            className={cn(
              "shadow-large overflow-visible border-2",
              authProfileTheme?.cardBorder,
            )}
          >
          <CardHeader className={cn(authProfileTheme?.cardBg)}>
            <CardTitle className={cn("text-center", authProfileTheme?.accentText)}>
              {isAdminProfile
                ? "Entrar como Administrador"
                : isOwnerProfile
                  ? activeTab === "cadastro"
                    ? "Cadastro de Proprietário"
                    : "Entrar como Proprietário"
                  : expectedProfile === "client"
                    ? activeTab === "cadastro"
                      ? "Cadastro de Hóspede"
                      : "Entrar como Hóspede"
                    : "Acesse sua conta"}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="overflow-visible">
              {!isAdminProfile && (
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login">Entrar</TabsTrigger>
                  <TabsTrigger value="cadastro">Cadastrar</TabsTrigger>
                </TabsList>
              )}

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">E-mail</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="login-senha">Senha</Label>
                    <Input
                      id="login-senha"
                      type="password"
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="lembrar-senha"
                        checked={lembrarSenha}
                        onCheckedChange={(checked) => setLembrarSenha(checked as boolean)}
                        disabled={loading}
                      />
                      <Label
                        htmlFor="lembrar-senha"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Lembrar senha
                      </Label>
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="text-sm p-0 h-auto"
                      onClick={() => navigate("/reset-password")}
                      disabled={loading}
                    >
                      Esqueci minha senha
                    </Button>
                  </div>

                  <Button
                    type="submit"
                    className={cn("w-full", authProfileTheme?.buttonClass)}
                    disabled={loading}
                  >
                    {loading ? "Entrando..." : "Entrar"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="cadastro" className="overflow-visible">
                {/* Regras de senha */}
                <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Requisitos da senha:</h4>
                  <div className="text-xs text-blue-700 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    <div className="flex items-center">
                      <span className="w-1 h-1 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                      Mínimo 8 caracteres
                    </div>
                    <div className="flex items-center">
                      <span className="w-1 h-1 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                      1 minúscula (a-z)
                    </div>
                    <div className="flex items-center">
                      <span className="w-1 h-1 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                      1 maiúscula (A-Z)
                    </div>
                    <div className="flex items-center">
                      <span className="w-1 h-1 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                      1 número (0-9)
                    </div>
                    <div className="flex items-center sm:col-span-2">
                      <span className="w-1 h-1 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                      1 caractere especial (!@#$%^&*...)
                    </div>
                  </div>
                </div>

                <form onSubmit={handleCadastro} className="space-y-4 overflow-visible">
                  <div>
                    <Label htmlFor="cadastro-nome">Nome Completo</Label>
                    <Input
                      id="cadastro-nome"
                      type="text"
                      placeholder="João Silva"
                      value={nomeCompleto}
                      onChange={(e) => setNomeCompleto(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="cadastro-email">E-mail</Label>
                    <Input
                      id="cadastro-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={handleEmailChange}
                      required
                      disabled={loading}
                      className={emailError ? "border-red-500" : ""}
                    />
                    {emailError && (
                      <p className="text-sm text-red-500 mt-1">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="cadastro-telefone">Telefone</Label>
                    <div className="flex gap-2">
                      <CountrySelect
                        value={phoneDdi}
                        onValueChange={(ddi: string, country: Country) => {
                          setPhoneDdi(ddi);
                        }}
                        disabled={loading}
                      />
                      <InputMask
                        mask="(99) 99999-9999"
                        value={telefone}
                        onChange={(e) => setTelefone(e.target.value)}
                        disabled={loading}
                        className="flex-1"
                      >
                        {(inputProps: any) => (
                          <Input
                            {...inputProps}
                            id="cadastro-telefone"
                            type="tel"
                            placeholder="(81) 99999-9999"
                            required
                            className="flex-1"
                          />
                        )}
                      </InputMask>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cadastro-senha">Senha</Label>
                    <Input
                      id="cadastro-senha"
                      type="password"
                      placeholder="••••••••"
                      value={senha}
                      onChange={handleSenhaChange}
                      required
                      disabled={loading}
                      className={senhaErrors.length > 0 ? "border-red-500" : ""}
                    />
                    {senhaErrors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-red-500 font-medium mb-1">A senha deve conter:</p>
                        <ul className="text-xs text-red-500 space-y-1">
                          {senhaErrors.map((error, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="cadastro-confirmar-senha">Confirmar Senha</Label>
                    <Input
                      id="cadastro-confirmar-senha"
                      type="password"
                      placeholder="••••••••"
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      required
                      disabled={loading}
                      className={confirmarSenha && !validatePasswordConfirmation(senha, confirmarSenha) ? "border-red-500" : ""}
                    />
                    {confirmarSenha && !validatePasswordConfirmation(senha, confirmarSenha) && (
                      <p className="text-sm text-red-500 mt-1">As senhas não coincidem</p>
                    )}
                  </div>

                  {/* Campo de token de indicação - apenas para proprietários */}
                  {isOwnerRegistration && (
                    <div>
                      <Label htmlFor="cadastro-token-indicacao">Token de Indicação (Opcional)</Label>
                      <Input
                        id="cadastro-token-indicacao"
                        type="text"
                        placeholder="ABCD1234EFGH5678"
                        value={partnerReferralToken}
                        onChange={handleReferralTokenChange}
                        maxLength={16}
                        disabled={loading}
                        className={partnerReferralToken && !validateReferralToken(partnerReferralToken) ? "border-red-500" : ""}
                      />
                      {partnerReferralToken && !validateReferralToken(partnerReferralToken) && (
                        <p className="text-sm text-red-500 mt-1">
                          O token deve ter exatamente 16 caracteres alfanuméricos (letras e números em maiúsculas)
                        </p>
                      )}
                      {partnerReferralToken && validateReferralToken(partnerReferralToken) && (
                        <p className="text-sm text-green-600 mt-1">✓ Token válido</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Se você foi indicado por um parceiro, insira o token de indicação fornecido
                      </p>
                    </div>
                  )}

                  <div className="flex items-start space-x-2 py-2">
                    <Checkbox
                      id="privacy-consent"
                      checked={privacyConsent}
                      onCheckedChange={(checked) => setPrivacyConsent(checked as boolean)}
                      disabled={loading}
                      className="mt-1 flex-shrink-0"
                    />
                    <Label
                      htmlFor="privacy-consent"
                      className="text-sm font-normal cursor-pointer leading-relaxed flex-1"
                    >
                      Eu concordo com a{" "}
                      <Link
                        to="/privacidade"
                        target="_blank"
                        className="text-primary hover:underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Política de Privacidade
                      </Link>
                      {" "}e autorizo o tratamento dos meus dados pessoais conforme a LGPD.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    className={cn("w-full", authProfileTheme?.buttonClass)}
                    disabled={loading || !privacyConsent}
                  >
                    {loading ? "Criando conta..." : "Criar Conta"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;